import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Stethoscope, Check, X, ExternalLink, Loader2, Filter } from "lucide-react";

/**
 * Admin panel — Clinician Trial Requests review queue.
 * Iteration 257. Lists pending applications, allows approve / reject (24h SLA).
 */
export default function ClinicianTrialRequestsPanel() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("pending");
  const [working, setWorking] = useState(null);
  const [rejectModal, setRejectModal] = useState({ open: false, id: null, reason: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/clinician-trials`, { params: { status: filter } });
      setItems(r.data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Грешка при зареждане");
    } finally {
      setLoading(false);
    }
  }, [API, filter]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    setWorking(id);
    try {
      await axios.post(`${API}/admin/clinician-trials/${id}/approve`);
      toast.success("Одобрено · 30-day Clinician trial активиран");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Грешка при одобрение");
    } finally {
      setWorking(null);
    }
  };

  const reject = async () => {
    if (!rejectModal.id) return;
    setWorking(rejectModal.id);
    try {
      await axios.post(`${API}/admin/clinician-trials/${rejectModal.id}/reject`, {
        reason: rejectModal.reason.trim() || null,
      });
      toast.success("Отказана");
      setRejectModal({ open: false, id: null, reason: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Грешка при отказ");
    } finally {
      setWorking(null);
    }
  };

  const cardCls = isDark
    ? "bg-[hsl(223,47%,12%)] border-[hsl(223,47%,20%)]"
    : "bg-white border-gray-100";

  const STATUS_COLORS = {
    pending:  isDark ? "bg-amber-500/15 text-amber-300" : "bg-amber-50 text-amber-700",
    approved: isDark ? "bg-emerald-500/15 text-emerald-300" : "bg-emerald-50 text-emerald-700",
    rejected: isDark ? "bg-rose-500/15 text-rose-300" : "bg-rose-50 text-rose-700",
  };

  return (
    <div data-testid="clinician-trials-panel">
      {/* Header + filter */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Stethoscope className={`w-5 h-5 ${isDark ? "text-rose-400" : "text-rose-500"}`} />
          <h2 className={`text-lg font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
            Clinician Trial Requests
          </h2>
          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
            isDark ? "bg-rose-500/15 text-rose-300" : "bg-rose-50 text-rose-700"
          }`}>
            {items.length}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <Filter className={`w-3.5 h-3.5 ${isDark ? "text-gray-400" : "text-gray-500"}`} />
          {["pending", "approved", "rejected", "all"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-2.5 py-1 rounded-md text-[11px] font-semibold ${
                filter === f
                  ? "bg-rose-500 text-white"
                  : isDark ? "bg-gray-800 text-gray-400 hover:text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
              data-testid={`clin-trials-filter-${f}`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-rose-500" />
        </div>
      ) : items.length === 0 ? (
        <Card className={cardCls}>
          <CardContent className="p-8 text-center">
            <Stethoscope className={`w-10 h-10 mx-auto mb-2 ${isDark ? "text-gray-700" : "text-gray-300"}`} />
            <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              Няма заявки със статус "{filter}".
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((it) => (
            <Card
              key={it.id}
              className={cardCls}
              data-testid={`clin-trial-row-${it.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${STATUS_COLORS[it.status] || ""}`}>
                        {it.status}
                      </span>
                      <span className={`text-sm font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                        {it.email}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-[12px] mt-2">
                      <div>
                        <div className={`text-[10px] uppercase font-semibold ${isDark ? "text-gray-500" : "text-gray-400"}`}>Специалност</div>
                        <div className={isDark ? "text-gray-200" : "text-gray-700"}>{it.specialty || "—"}</div>
                      </div>
                      <div>
                        <div className={`text-[10px] uppercase font-semibold ${isDark ? "text-gray-500" : "text-gray-400"}`}>УИН</div>
                        <div className={`font-mono ${isDark ? "text-gray-200" : "text-gray-700"}`}>{it.license_number || "—"}</div>
                      </div>
                      <div>
                        <div className={`text-[10px] uppercase font-semibold ${isDark ? "text-gray-500" : "text-gray-400"}`}>Подадена</div>
                        <div className={isDark ? "text-gray-200" : "text-gray-700"}>
                          {it.created_at ? new Date(it.created_at).toLocaleDateString("bg") : "—"}
                        </div>
                      </div>
                    </div>
                    {it.notes && (
                      <p className={`text-[11px] mt-2 italic ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                        "{it.notes}"
                      </p>
                    )}
                    {it.license_document_url && (
                      <a
                        href={it.license_document_url.startsWith("http") ? it.license_document_url : `${API}/files/${it.license_document_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`inline-flex items-center gap-1 mt-2 text-[11px] font-semibold underline-offset-2 hover:underline ${
                          isDark ? "text-rose-300" : "text-rose-600"
                        }`}
                        data-testid={`clin-trial-doc-${it.id}`}
                      >
                        <ExternalLink className="w-3 h-3" /> Виж документ
                      </a>
                    )}
                    {it.reject_reason && (
                      <p className={`text-[11px] mt-2 ${isDark ? "text-rose-300" : "text-rose-600"}`}>
                        Причина за отказ: {it.reject_reason}
                      </p>
                    )}
                  </div>
                  {it.status === "pending" && (
                    <div className="flex gap-1.5 flex-shrink-0">
                      <Button
                        size="sm"
                        className="h-8 text-xs bg-emerald-500 hover:bg-emerald-600 text-white"
                        onClick={() => approve(it.id)}
                        disabled={working === it.id}
                        data-testid={`clin-trial-approve-${it.id}`}
                      >
                        {working === it.id
                          ? <Loader2 className="w-3 h-3 animate-spin" />
                          : <><Check className="w-3 h-3 mr-1" /> Одобри</>}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 text-xs border-rose-500 text-rose-500 hover:bg-rose-50"
                        onClick={() => setRejectModal({ open: true, id: it.id, reason: "" })}
                        disabled={working === it.id}
                        data-testid={`clin-trial-reject-${it.id}`}
                      >
                        <X className="w-3 h-3 mr-1" /> Отхвърли
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Reject modal */}
      {rejectModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="reject-modal">
          <div className={`max-w-md w-full rounded-2xl p-5 ${cardCls} border`}>
            <h3 className={`text-base font-bold mb-2 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
              Отхвърли заявка
            </h3>
            <p className={`text-[12px] mb-3 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              Причината ще бъде изпратена на потребителя по email (опционално).
            </p>
            <textarea
              value={rejectModal.reason}
              onChange={(e) => setRejectModal({ ...rejectModal, reason: e.target.value })}
              placeholder="Напр. Документът е нечетлив / УИН не е намерен в регистъра..."
              rows={3}
              className={`w-full px-3 py-2 rounded-md border text-sm ${
                isDark ? "bg-[hsl(223,47%,15%)] border-[hsl(223,47%,20%)] text-white" : "bg-white border-gray-200"
              }`}
              data-testid="reject-reason-input"
            />
            <div className="flex gap-2 mt-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setRejectModal({ open: false, id: null, reason: "" })}
                data-testid="reject-cancel-btn"
              >
                Отказ
              </Button>
              <Button
                className="flex-1 bg-rose-500 hover:bg-rose-600 text-white"
                onClick={reject}
                disabled={working === rejectModal.id}
                data-testid="reject-confirm-btn"
              >
                {working === rejectModal.id ? <Loader2 className="w-3 h-3 animate-spin" /> : "Отхвърли"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
